/**
 * ============================================================================
 * useProformaSimulations — S19.4/S19.5
 * ============================================================================
 * Hooks para listar, clonar y mutar simulaciones pro-forma.
 *
 * - useProformaSimulations(): lista todas las simulaciones del usuario en la
 *   organización activa.
 * - useProformaSimulation(id): obtiene una simulación con su payload JSONB.
 * - useClonarEmbarqueAProforma(): mutación que invoca el RPC server-side y
 *   navega/invalida cache.
 * - useUpdateSimulationParams(): persistencia opcional de los sliders en el
 *   payload (debounced en el componente).
 * ============================================================================
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { CostEngineInput, EngineItemInput, BenefitMatrix } from "@/lib/landedCostEngine";
import type { Incoterm } from "@/lib/expensePresets";

/**
 * Bloque comercial de la cotización (Fase 1 — solo frontend, vive en payload).
 * NO afecta el cálculo del motor; solo alimenta la UI y el export Excel.
 */
export interface ProformaComercial {
  incoterm: Incoterm;
  puerto_carga?: string;
  puerto_destino?: string;
  transito_dias?: number;
  dias_libres_destino?: number;
  tiempo_nacionalizacion_dias?: number;
  validez_dias?: number;
  condiciones_pago?: string;
  observaciones?: string;
  sobrecostos_posibles?: string[];
}

/**
 * V2 — estructura legacy con dos listas separadas (snapshot + nuevos) y
 * overrides parciales en `sim_params`. Sigue siendo el shape de todas las
 * simulaciones existentes hasta que el usuario las migre (Fase 4).
 */
export interface ProformaPayloadV2 {
  schema_version: "v2";
  embarque_base: {
    id: string;
    numero_do: string;
    proveedor: string | null;
    trm_aduana: number | null;
    modo_transporte: string | null;
  } | null;
  cost_engine_input: CostEngineInput;
  sim_params: {
    /** @deprecated Slider eliminado — TRM se edita en Centro de Monedas. */
    ajuste_trm_pct?: number;
    /** @deprecated Slider eliminado — gastos se editan directamente. */
    ajuste_flete_pct?: number;
    /** S19.8: overrides a gastos existentes (id → override parcial o monto numérico legacy). */
    gastos_editados?: Record<string, number | SimulatedGastoOverride>;
    /** S19.8: gastos nuevos inyectados en el escenario (imprevistos). */
    gastos_nuevos?: SimulatedGasto[];
    /** S19.10: overrides parciales sobre ítems del snapshot (key = item.id base). */
    items_editados?: Record<string, SimulatedItemOverride>;
    /** S19.10: ítems nuevos inyectados en el escenario. */
    items_nuevos?: SimulatedItem[];
    /** S19.10: overrides de % arancel por itemId (incluye base y nuevos). */
    arancel_pct_overrides?: Record<string, number>;
    /** S19.10: overrides de % IVA por itemId (incluye base y nuevos). */
    iva_pct_overrides?: Record<string, number>;
    /** Fase 1.3 — overrides de régimen aduanero por itemId (decisión explícita del usuario). */
    beneficio_overrides?: Record<string, BenefitMatrix>;
    /** Fase 1 cotización — bloque comercial (incoterm, días libres, etc.). */
    comercial?: ProformaComercial;
    /** Centro de Monedas — overrides de TRM por moneda (vacío = usar valor de mercado). */
    trm_overrides?: Record<string, number>;
  };
  cloned_at?: string;
}

/**
 * Ítem plano del escenario (V3). Shape idéntico al que consume el RPC
 * `calculate_landed_cost_preview`. Sin distinción snapshot/nuevo.
 */
export type ItemPlano = EngineItemInput;

/**
 * Gasto plano del escenario (V3). Reutiliza `SimulatedGasto` (concepto +
 * monto + base + iva_local). El motor lo transforma a `EngineGastoInput`.
 */
export type GastoPlano = SimulatedGasto;

/**
 * V3 — estructura unificada: una sola lista de ítems y una sola de gastos.
 * El "snapshot" es opcional y se guarda como checkpoint (no como lista separada).
 */
export interface ProformaPayloadV3 {
  schema_version: "v3";
  embarque_base: {
    id: string;
    numero_do: string;
    proveedor: string | null;
    trm_aduana: number | null;
    modo_transporte: string | null;
  } | null;
  /**
   * Estado del motor preservado (TRM, documents, compliance, engineVersion).
   * Sus arrays `items` y `gastos` se mantienen vacíos en V3 — la fuente de
   * verdad son los arrays planos `items` y `gastos` de este payload.
   */
  cost_engine_input: CostEngineInput;
  /** Lista única y editable de ítems del escenario. */
  items: ItemPlano[];
  /** Lista única y editable de gastos del escenario. */
  gastos: GastoPlano[];
  /** Checkpoint opcional (línea base) creado por el usuario. */
  snapshot?: {
    cost_engine_input: CostEngineInput;
    rpc_result: unknown;
    saved_at: string;
  };
  sim_params: {
    /** Bloque comercial (incoterm, días libres, validez, etc.). */
    comercial?: ProformaComercial;
    /** Centro de Monedas — overrides de TRM por moneda (vacío = usar valor de mercado). */
    trm_overrides?: Record<string, number>;
  };
  cloned_at?: string;
}

/** Union de versiones soportadas. */
export type AnyProformaPayload = ProformaPayloadV2 | ProformaPayloadV3;

/**
 * Alias retrocompatible — todo el código existente sigue tipando como V2.
 * El runtime tolera v3 (campos undefined caen a `?? {}` o `?? []`).
 * Fase 2 reemplazará consumidores por `AnyProformaPayload` con narrowing.
 */
export type ProformaPayload = ProformaPayloadV2;

/**
 * Detecta la versión del payload de forma defensiva. Cualquier payload sin
 * `schema_version` explícito se interpreta como "v2" (legacy).
 */
export function getSchemaVersion(payload: unknown): "v2" | "v3" {
  const v = (payload as { schema_version?: unknown } | null | undefined)?.schema_version;
  return v === "v3" ? "v3" : "v2";
}

/**
 * Type guard: ¿es v2?
 */
export function isPayloadV2(payload: unknown): payload is ProformaPayloadV2 {
  return getSchemaVersion(payload) === "v2";
}

/**
 * Type guard: ¿es v3?
 */
export function isPayloadV3(payload: unknown): payload is ProformaPayloadV3 {
  return getSchemaVersion(payload) === "v3";
}

/**
 * Migra un payload V2 a V3 fusionando snapshot + overrides + nuevos en una
 * sola lista plana. **Función pura** — no toca BD.
 *
 * Reglas de fusión:
 *   - Items base: aplica `items_editados[id]` y `beneficio_overrides[id]`.
 *   - Items nuevos: se concatenan al final.
 *   - Gastos base: aplica `gastos_editados[id]` (monto + iva_local + base).
 *   - Gastos nuevos: se concatenan al final.
 *   - Compliance overrides (% arancel/IVA por itemId) se preservan dentro
 *     de `cost_engine_input.compliance` para que el RPC los siga aplicando.
 *   - Sliders `ajuste_trm_pct` y `ajuste_flete_pct` NO se persisten (eran
 *     estado de UI temporal); se descartan al migrar.
 *
 * NOTA: NO se usa todavía. Preparado para el botón "Aplanar" de Fase 4.
 */
export function migrateV2toV3(v2: ProformaPayloadV2): ProformaPayloadV3 {
  const sp = v2.sim_params ?? ({} as ProformaPayloadV2["sim_params"]);
  const editados = sp.items_editados ?? {};
  const nuevos = sp.items_nuevos ?? [];
  const benOverrides = sp.beneficio_overrides ?? {};
  const arancelOverrides = sp.arancel_pct_overrides ?? {};
  const ivaOverrides = sp.iva_pct_overrides ?? {};
  const gastosEd = sp.gastos_editados ?? {};
  const gastosNuevos = sp.gastos_nuevos ?? [];

  // 1) Fusionar ítems base con overrides
  const itemsFusionados: ItemPlano[] = (v2.cost_engine_input.items ?? []).map((it) => {
    const ov = editados[it.id];
    const benOv = benOverrides[it.id];
    if (!ov && !benOv) return it;
    const cantidad = ov?.cantidad ?? Number(it.cantidad ?? 0);
    const precio = ov?.precio_unitario ?? Number(it.precio_unitario ?? 0);
    const subTotalRecalc = Number(cantidad) * Number(precio);
    return {
      ...it,
      cantidad,
      precio_unitario: precio,
      sub_total: ov?.sub_total ?? subTotalRecalc,
      peso_bruto_kg: ov?.peso_bruto_kg ?? it.peso_bruto_kg,
      volumen_cbm:
        ov?.volumen_cbm ?? (it as { volumen_cbm?: number | null }).volumen_cbm ?? null,
      aplica_tlc: ov?.aplica_tlc ?? it.aplica_tlc,
      beneficio_aduanero: benOv ?? ov?.beneficio_aduanero ?? it.beneficio_aduanero,
      moneda: (ov?.moneda ?? (it as { moneda?: string | null }).moneda) ?? null,
      hs_code:
        (((ov as { hs_code?: string | null } | undefined)?.hs_code ??
          (it as { hs_code?: string | null }).hs_code) ?? "")
          .toString()
          .trim() || null,
      grados_alcoholicos:
        ov?.grados_alcoholicos ??
        (it as { grados_alcoholicos?: number | null }).grados_alcoholicos ??
        null,
      litros_por_unidad:
        ov?.litros_por_unidad ??
        (it as { litros_por_unidad?: number | null }).litros_por_unidad ??
        null,
      tarifa_ico:
        ov?.tarifa_ico ?? (it as { tarifa_ico?: number | null }).tarifa_ico ?? null,
      ico_tipo:
        (ov as { ico_tipo?: string | null } | undefined)?.ico_tipo ??
        (it as { ico_tipo?: string | null }).ico_tipo ??
        null,
    } as ItemPlano;
  });

  // 2) Concatenar ítems nuevos
  const itemsInyectados: ItemPlano[] = nuevos.map((it) => ({
    id: it.id,
    documento_id: it.documento_id,
    sku: it.sku ?? null,
    descripcion: it.descripcion ?? null,
    cantidad: Number(it.cantidad ?? 0),
    precio_unitario: Number(it.precio_unitario ?? 0),
    sub_total:
      Number(it.sub_total ?? Number(it.cantidad ?? 0) * Number(it.precio_unitario ?? 0)),
    peso_bruto_kg: Number(it.peso_bruto_kg ?? 0),
    volumen_cbm: it.volumen_cbm ?? null,
    aplica_tlc: it.aplica_tlc ?? false,
    beneficio_aduanero: benOverrides[it.id] ?? it.beneficio_aduanero ?? "GENERAL",
    moneda: (it.moneda ?? "USD").toString().toUpperCase(),
    hs_code: (it.hs_code ?? "").toString().trim() || null,
    grados_alcoholicos: it.grados_alcoholicos ?? null,
    litros_por_unidad: it.litros_por_unidad ?? null,
    tarifa_ico: it.tarifa_ico ?? null,
    ico_tipo: (it as { ico_tipo?: string | null }).ico_tipo ?? null,
  } as ItemPlano));

  // 3) Fusionar gastos base con overrides
  const gastosFusionados: GastoPlano[] = (v2.cost_engine_input.gastos ?? []).map((g) => {
    const rawOv = gastosEd[g.id];
    const ov: SimulatedGastoOverride | undefined =
      typeof rawOv === "number" ? { monto: rawOv } : rawOv;
    return {
      id: g.id,
      concepto: ov?.concepto ?? g.concepto,
      monto: ov?.monto ?? Number(g.monto ?? 0),
      iva_local: Number(ov?.iva_local ?? 0),
      moneda: (ov?.moneda ?? g.moneda ?? "COP").toString().toUpperCase(),
      base_prorrateo:
        (ov?.base_prorrateo as GastoPlano["base_prorrateo"]) ??
        (g.base_prorrateo as GastoPlano["base_prorrateo"]),
      aplica_costeo_inventario: ov?.aplica_costeo_inventario ?? g.aplica_costeo_inventario ?? true,
    };
  });

  // 4) Preservar compliance: snapshot maps + overrides aplicados encima
  const newComplianceArancel: Record<string, number> = {
    ...(v2.cost_engine_input.compliance?.arancelPctByItemId ?? {}),
    ...arancelOverrides,
  };
  const newComplianceIva: Record<string, number> = {
    ...(v2.cost_engine_input.compliance?.ivaPctByItemId ?? {}),
    ...ivaOverrides,
  };

  // El cost_engine_input se preserva como "estado del motor" (TRM, documents,
  // engineVersion). Sus arrays internos se vacían — la fuente de verdad ahora
  // son los arrays planos del payload V3.
  const ceiV3: CostEngineInput = {
    ...v2.cost_engine_input,
    items: [],
    gastos: [],
    compliance: {
      arancelPctByItemId: newComplianceArancel,
      ivaPctByItemId: newComplianceIva,
    },
  };

  return {
    schema_version: "v3",
    embarque_base: v2.embarque_base,
    cost_engine_input: ceiV3,
    items: [...itemsFusionados, ...itemsInyectados],
    gastos: [...gastosFusionados, ...gastosNuevos],
    sim_params: {
      comercial: sp.comercial,
    },
    cloned_at: v2.cloned_at,
  };
}

/** Override parcial sobre un ítem base (solo campos editables). */
export type SimulatedItemOverride = Partial<
  Pick<EngineItemInput, "cantidad" | "precio_unitario" | "peso_bruto_kg" | "volumen_cbm" | "sub_total" | "aplica_tlc" | "beneficio_aduanero" | "unidad_medida" | "grados_alcoholicos" | "litros_por_unidad" | "tarifa_ico" | "ico_tipo">
> & {
  /** Override de moneda del ítem (afecta documento sintético en motor). */
  moneda?: string;
  /** Override de partida arancelaria (HS Code). */
  hs_code?: string | null;
};

/** Override parcial sobre un gasto base del snapshot (Fase B — edición de monto/base/IVA local). */
export interface SimulatedGastoOverride {
  monto?: number;
  base_prorrateo?: SimulatedGasto["base_prorrateo"];
  iva_local?: number;
  /** Override de concepto (editable en gestor). */
  concepto?: string;
  /** Override de moneda. */
  moneda?: string;
  /** Override de aplicación al costeo de inventario (toggle inhabilitar). */
  aplica_costeo_inventario?: boolean;
}

/** Unidades de medida soportadas en el simulador. */
export type SimulatedUnidad = "UN" | "KG" | "CBM" | "LTS" | "PAR";

/** Ítem nuevo inyectado en el escenario. Compatible con EngineItemInput. */
export interface SimulatedItem {
  id: string;
  documento_id: string;
  sku?: string | null;
  descripcion?: string | null;
  unidad?: SimulatedUnidad | null;
  cantidad: number;
  precio_unitario: number;
  sub_total: number;
  peso_bruto_kg?: number | null;
  volumen_cbm?: number | null;
  /** Moneda del ítem (cualquier código presente en trm_historico). Default: 'USD'. */
  moneda?: string | null;
  /** Partida arancelaria (HS Code). El RPC resuelve gravamen/IVA via arancel_1881. */
  hs_code?: string | null;
  aplica_tlc?: boolean | null;
  beneficio_aduanero?: BenefitMatrix | null;
  /** ICO — grados alcohólicos (0–100). Opcional, solo para bebidas. */
  grados_alcoholicos?: number | null;
  /** ICO — litros líquido por unidad. */
  litros_por_unidad?: number | null;
  /** ICO — tarifa COP por grado-alcohólico-litro. */
  tarifa_ico?: number | null;
  /** ICO multi-tipo: null/'BEBIDAS_ALCOHOLICAS' (g·t·l), 'VEHICULOS' (% CIF), 'CIGARRILLOS'. */
  ico_tipo?: string | null;
}

/** Gasto editable/nuevo dentro de la simulación (vive solo en payload.sim_params). */
export interface SimulatedGasto {
  id: string;
  concepto: string;
  monto: number;
  /** IVA local editable en simulación; se suma al monto enviado al motor. */
  iva_local?: number;
  moneda: string;                     // "COP" | "USD" | "EUR" | ...
  base_prorrateo: "Peso" | "Valor" | "CBM" | "Ninguna" | "SIN_CLASIFICAR";
  aplica_costeo_inventario?: boolean;
}

export interface ProformaSimulation {
  id: string;
  org_id: string;
  user_id: string;
  nombre: string;
  descripcion: string | null;
  embarque_base_id: string | null;
  status: string;
  notas: string | null;
  payload: ProformaPayload;
  created_at: string;
  updated_at: string;
}

export function useProformaSimulations(opts?: { activeOnly?: boolean }) {
  const { orgId } = useAuth();
  const activeOnly = opts?.activeOnly ?? false;

  return useQuery<ProformaSimulation[]>({
    queryKey: ["proforma-simulations", orgId, { activeOnly }],
    enabled: !!orgId,
    queryFn: async () => {
      let q = supabase
        .from("simulaciones_proforma")
        .select("*")
        .eq("org_id", orgId!)
        .is("deleted_at", null)
        .order("updated_at", { ascending: false });
      if (activeOnly) q = q.neq("status", "materialized");
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as ProformaSimulation[];
    },
  });
}

export function useProformaSimulation(id: string | null | undefined) {
  return useQuery<ProformaSimulation | null>({
    queryKey: ["proforma-simulation", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("simulaciones_proforma")
        .select("*")
        .eq("id", id!)
        .is("deleted_at", null)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as ProformaSimulation) ?? null;
    },
  });
}

export function useClonarEmbarqueAProforma() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { embarqueId: string; nombre?: string }) => {
      // 1. RPC server-side crea la simulación con payload V2 legacy
      const { data, error } = await supabase.rpc("clonar_embarque_a_proforma" as never, {
        p_embarque_id: params.embarqueId,
        p_nombre: params.nombre ?? null,
      } as never);
      if (error) throw error;
      const newId = data as unknown as string;

      // 2. Post-migrar V2 → V3 (Fase 1) — las nuevas simulaciones nacen en V3.
      //    Las existentes siguen leyéndose como V2 hasta que el usuario use
      //    el botón "Aplanar" (Fase 4).
      try {
        const { data: row, error: readErr } = await supabase
          .from("simulaciones_proforma")
          .select("payload")
          .eq("id", newId)
          .maybeSingle();
        if (readErr) throw readErr;
        const rawPayload = row?.payload as unknown;
        if (rawPayload && getSchemaVersion(rawPayload) === "v2") {
          const v3 = migrateV2toV3(rawPayload as ProformaPayloadV2);
          const { error: updErr } = await supabase
            .from("simulaciones_proforma")
            .update({ payload: v3 as unknown as never })
            .eq("id", newId);
          if (updErr) throw updErr;
        }
      } catch (e) {
        // Si la migración falla, la simulación queda en V2 (degradación segura).
        // El usuario podrá migrarla manualmente con el botón "Aplanar" (Fase 4).
        console.warn("[clonar] Post-migración V2→V3 falló, queda como V2:", e);
      }

      return newId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proforma-simulations"] });
    },
  });
}

export function useUpdateSimulationPayload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { id: string; payload: ProformaPayload; nombre?: string }) => {
      const update: Record<string, unknown> = { payload: params.payload as unknown as never };
      if (params.nombre !== undefined) update.nombre = params.nombre;
      const { error } = await supabase
        .from("simulaciones_proforma")
        .update(update)
        .eq("id", params.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ["proforma-simulation", vars.id] });
      queryClient.invalidateQueries({ queryKey: ["proforma-simulations"] });
    },
  });
}

export function useDeleteSimulation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // Fase 3.1 — Soft-delete: alineado con resto del sistema (deleted_at).
      const { error } = await supabase
        .from("simulaciones_proforma")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["proforma-simulations"] }),
  });
}
